import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { AgentLinkFormValue, DialogStageFormValue, UpdateFormValues } from '../lib/types';

export const useFormSchema = (agentId?: number): z.ZodType<UpdateFormValues> => {
  const { t } = useTranslation('translation');

  const required = t('common.formErrors.required');

  const optionalText = () => z.string().trim();

  const requiredText = () => z.string().trim().min(1, required);

  const stageSchema: z.ZodType<DialogStageFormValue> = z.object({
    order: z.number().int().min(0),
    agent_field_id: z.number().int().min(1, required),

    prompt: requiredText(),
  });

  const linkSchema: z.ZodType<AgentLinkFormValue> = z.object({
    target_agent_id: z.number().int().min(1, required),
    prompt: requiredText(),
  });

  return z
    .object({
      name: requiredText(),
      role: optionalText(),
      style: optionalText(),
      recommendations: optionalText(),
      charge_method: z.enum(['chat', 'lead'], { required_error: required }),

      stages: z.array(stageSchema),

      agent_links: z.array(linkSchema),

      show_form_prompts: z.array(optionalText()),

      rules: z.array(optionalText()),

      exceptions: z.array(optionalText()),
    })
    .superRefine((values, ctx) => {
      const usedTargets = new Set<number>();

      values.agent_links.forEach((link, index) => {
        const targetAgentId = Number(link.target_agent_id);
        if (!Number.isFinite(targetAgentId) || targetAgentId <= 0) return;

        if (agentId && link.target_agent_id === agentId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('prompt.linkedAgents.errors.self'),
            path: ['agent_links', index, 'target_agent_id'],
          });
        }

        if (usedTargets.has(targetAgentId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('prompt.linkedAgents.errors.duplicate'),
            path: ['agent_links', index, 'target_agent_id'],
          });
        } else {
          usedTargets.add(targetAgentId);
        }
      });
    });
};
